/**
 * BomFormDialog — bám Access spec `frmFactoryMDQryBOMAdd` + sub-forms
 * `frmFactoryMDQryBOM_Input` / `frmFactoryMDQryBOM_Output`.
 *
 * Visible user-editable fields (TabIndex Access):
 *   1. bom_code (required, disabled trong edit mode — là business key)
 *   2. bom_type (required, ComboBox load từ /bom/options.bomTypes)
 *   3. processing_loss (Percent)
 *   4. moisture_loss (Percent)
 *   5. note (multiline)
 *   6. Sub-form Input: list quality_code + quality_value (Percent)
 *   7. Sub-form Output: list quality_code + quality_value (Percent)
 *
 * Hidden system fields KHÔNG render: factory_bom_id, is_status, created_*,
 * updated_*. Quality picker options load từ /bom/options.qualityItems (=
 * list_and_valo).
 *
 * Validation (theo VBA btnSave_Click):
 *   - errCheck=1: bom_code empty → "Please enter valid BOM Code"
 *   - errCheck=2: bom_code đã tồn tại (NEW mode) → BE trả 400, em show snack
 *   - errCheck=3: bom_type empty → "Please type BOM Type"
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'

import type {
  BomChildRow,
  BomDetail,
  BomOptions,
  BomSavePayload,
} from '@/store/api/factoryApi'
import { FORM_GRID_2COL, TEXT_TRUNCATE } from '@/theme/responsive'

interface DraftChild {
  quality_code: string
  quality_value: string
}

interface DraftState {
  bom_code: string
  bom_type: string
  crop_year: string
  processing_loss: string
  moisture_loss: string
  note: string
  inputs: DraftChild[]
  outputs: DraftChild[]
}

const EMPTY_DRAFT: DraftState = {
  bom_code: '',
  bom_type: '',
  crop_year: '',
  processing_loss: '',
  moisture_loss: '',
  note: '',
  inputs: [],
  outputs: [],
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Skill `/convert-form` readonly bg standard — neutral gray cho PK lock edit.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
  },
}

/** Shared widths — Input/Output 2 bảng align cùng tỉ lệ cột. */
const PERCENT_COL_WIDTH = 130
const DELETE_COL_WIDTH = 36

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

/** Value DB lưu dạng 0..1 (Access Format=Percent). UI hiển thị ×100 cho khớp
 *  suffix `%`. Dùng toFixed(6) → parseFloat để cắt sai số floating-point
 *  (0.07 × 100 = 7.000000000000001). */
function numToPercentStr(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return ''
  return String(parseFloat((n * 100).toFixed(6)))
}

function percentStrToNum(s: string): number | null {
  const t = s.trim()
  if (!t) return null
  const n = Number(t)
  if (!Number.isFinite(n)) return null
  return parseFloat((n / 100).toFixed(8))
}

function detailToDraftChildren(rows: BomChildRow[]): DraftChild[] {
  return rows.map((r) => ({
    quality_code: String(r.quality_code ?? ''),
    quality_value: numToPercentStr(
      typeof r.quality_value === 'number' ? r.quality_value : null,
    ),
  }))
}

function detailToDraft(detail: BomDetail | null): DraftState {
  if (!detail) return { ...EMPTY_DRAFT }
  return {
    bom_code: detail.bomCode ?? '',
    bom_type: detail.bomType ?? '',
    crop_year: detail.cropYear ?? '',
    processing_loss: numToPercentStr(detail.processingLoss),
    moisture_loss: numToPercentStr(detail.moistureLoss),
    note: detail.note ?? '',
    inputs: detailToDraftChildren(detail.inputs ?? []),
    outputs: detailToDraftChildren(detail.outputs ?? []),
  }
}

function draftToPayload(draft: DraftState): BomSavePayload {
  return {
    bom_code: draft.bom_code.trim(),
    bom_type: draft.bom_type.trim(),
    crop_year: draft.crop_year.trim(),
    processing_loss: percentStrToNum(draft.processing_loss),
    moisture_loss: percentStrToNum(draft.moisture_loss),
    note: draft.note.trim() || null,
    inputs: draft.inputs
      .filter((r) => r.quality_code.trim())
      .map((r) => ({
        quality_code: r.quality_code.trim(),
        quality_value: percentStrToNum(r.quality_value),
      })),
    outputs: draft.outputs
      .filter((r) => r.quality_code.trim())
      .map((r) => ({
        quality_code: r.quality_code.trim(),
        quality_value: percentStrToNum(r.quality_value),
      })),
  }
}

interface BomFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  detail: BomDetail | null
  saving: boolean
  /** Loading detail trong edit mode (network fetch). */
  detailLoading?: boolean
  options: BomOptions | null
  optionsLoading: boolean
  /** Crop year hiện đang filter trên page — dùng làm default khi mode='new'. */
  defaultCropYear?: string
  /** Permission gating — false → ẩn Save + show read-only banner (defensive layer). */
  canEdit?: boolean
  onClose: () => void
  onSave: (payload: BomSavePayload) => void
}

interface QualityPickerProps {
  title: string
  rows: DraftChild[]
  pickerOptions: BomOptions['qualityItems']
  error?: boolean
  onChange: (next: DraftChild[]) => void
}

function QualityPicker({ title, rows, pickerOptions, error, onChange }: QualityPickerProps) {
  // Multi-select autocomplete: selected = rows.map(quality_code). Khi user
  // tick một code mới → push DraftChild với quality_value rỗng. Khi user
  // un-tick → filter out. Edit quality_value qua inline TextField mỗi row.
  const selectedCodes = useMemo(() => rows.map((r) => r.quality_code), [rows])
  const options = useMemo(
    () => pickerOptions.map((o) => String(o.code)),
    [pickerOptions],
  )

  // Refs cho mỗi row's value input → ArrowUp/Down/Enter di chuyển focus
  // giữa các Quality (spreadsheet-style), không trigger spinner.
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const handleAutocompleteChange = (_: unknown, codes: string[]) => {
    const existing = new Map(rows.map((r) => [r.quality_code, r]))
    const next: DraftChild[] = codes.map(
      (code) => existing.get(code) ?? { quality_code: code, quality_value: '' },
    )
    onChange(next)
  }

  const handleValueChange = (idx: number, value: string) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, quality_value: value } : r))
    onChange(next)
  }

  const handleRemove = (idx: number) => {
    onChange(rows.filter((_, i) => i !== idx))
  }

  const focusRow = (idx: number) => {
    const el = inputRefs.current[idx]
    if (!el) return
    el.focus()
    // Select all để user gõ giá trị mới đè ngay, không phải Ctrl+A.
    el.select()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>, idx: number) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter') {
      if (idx < rows.length - 1) {
        e.preventDefault()
        focusRow(idx + 1)
      }
    } else if (e.key === 'ArrowUp') {
      if (idx > 0) {
        e.preventDefault()
        focusRow(idx - 1)
      }
    }
  }

  return (
    <Stack spacing={1}>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        color={error ? 'error.main' : 'inherit'}
      >
        {title}
        {error ? ' *' : ''}
      </Typography>
      <Autocomplete
        multiple
        size="small"
        options={options}
        value={selectedCodes}
        onChange={handleAutocompleteChange}
        disableCloseOnSelect
        sx={compactInputSx}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add quality codes"
            placeholder={selectedCodes.length ? '' : 'Pick item codes'}
            error={error}
            helperText={error ? 'Required' : undefined}
          />
        )}
      />
      {rows.length ? (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              bgcolor: 'action.hover',
              borderBottom: 1,
              borderColor: 'divider',
              fontSize: '0.72rem',
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>Quality Code</Box>
            <Box sx={{ width: PERCENT_COL_WIDTH, textAlign: 'right', pr: 1 }}>Percent</Box>
            <Box sx={{ width: DELETE_COL_WIDTH }} />
          </Box>
          {/* Data rows */}
          {rows.map((r, idx) => (
            <Box
              key={`${r.quality_code}-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.5,
                borderTop: idx === 0 ? 0 : 1,
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={r.quality_code}
              >
                {r.quality_code}
              </Box>
              <TextField
                size="small"
                value={r.quality_value}
                onChange={(e) => handleValueChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                inputRef={(el: HTMLInputElement | null) => {
                  inputRefs.current[idx] = el
                }}
                sx={{ width: PERCENT_COL_WIDTH, ...compactInputSx }}
                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              <Box sx={{ width: DELETE_COL_WIDTH, display: 'flex', justifyContent: 'center' }}>
                <IconButton size="small" onClick={() => handleRemove(idx)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          No rows yet — pick quality codes above.
        </Typography>
      )}
    </Stack>
  )
}

export default function BomFormDialog({
  open,
  mode,
  detail,
  saving,
  detailLoading,
  options,
  optionsLoading,
  defaultCropYear,
  canEdit = true,
  onClose,
  onSave,
}: BomFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setTouched(false)
    if (mode === 'edit') {
      setDraft(detailToDraft(detail))
    } else {
      setDraft({ ...EMPTY_DRAFT, crop_year: defaultCropYear ?? '' })
    }
  }, [open, mode, detail, defaultCropYear])

  const filledInputs = useMemo(
    () => draft.inputs.filter((r) => r.quality_code.trim()).length,
    [draft.inputs],
  )
  const filledOutputs = useMemo(
    () => draft.outputs.filter((r) => r.quality_code.trim()).length,
    [draft.outputs],
  )
  const bomCodeError = touched && !draft.bom_code.trim()
  const bomTypeError = touched && !draft.bom_type.trim()
  const cropYearError = touched && !draft.crop_year.trim()
  const inputsError = touched && filledInputs === 0
  const outputsError = touched && filledOutputs === 0
  const validationError = bomCodeError
    ? 'BOM Code is required'
    : cropYearError
    ? 'Crop Year is required'
    : bomTypeError
    ? 'BOM Type is required'
    : inputsError
    ? 'At least one Quality - Input code is required'
    : outputsError
    ? 'At least one Quality - Output code is required'
    : ''

  const handleSave = () => {
    if (!canEdit) return
    setTouched(true)
    if (
      !draft.bom_code.trim() ||
      !draft.bom_type.trim() ||
      !draft.crop_year.trim() ||
      filledInputs === 0 ||
      filledOutputs === 0
    ) {
      return
    }
    onSave(draftToPayload(draft))
  }

  const bomTypeOptions = options?.bomTypes ?? []
  const qualityOptions = options?.qualityItems ?? []
  // Form New cho phép chọn crop year kế tiếp (chưa có row nào trong DB) để
  // chuẩn bị mùa tới — prepend nextCropYear vào đầu danh sách nếu chưa có.
  const cropYearOptions = useMemo(() => {
    const base = options?.cropYears ?? []
    const nxt = options?.nextCropYear
    if (!nxt || base.includes(nxt)) return base
    return [nxt, ...base]
  }, [options?.cropYears, options?.nextCropYear])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            variant="subtitle1"
            fontWeight={800}
            noWrap
            title={mode === 'new' ? 'New BOM' : `Edit BOM — ${draft.bom_code}${draft.crop_year ? ` (${draft.crop_year})` : ''}`}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0 }}
          >
            {mode === 'new'
              ? 'New BOM'
              : `Edit BOM — ${draft.bom_code}${draft.crop_year ? ` (${draft.crop_year})` : ''}`}
          </Typography>
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={saving}
              sx={titleBtnSx}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!canEdit || saving || detailLoading}
              startIcon={saving ? <CircularProgress size={12} color="inherit" /> : null}
              sx={titleBtnSx}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5 }}>
        {detailLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {!canEdit ? (
              <Alert severity="info">Read-only — you don't have edit permission for this factory.</Alert>
            ) : null}
            {validationError ? <Alert severity="error">{validationError}</Alert> : null}

            <Box sx={FORM_GRID_2COL}>
              <Autocomplete
                freeSolo
                size="small"
                options={cropYearOptions}
                value={draft.crop_year}
                onChange={(_, v) => setDraft({ ...draft, crop_year: v ?? '' })}
                onInputChange={(_, v) => setDraft({ ...draft, crop_year: v })}
                loading={optionsLoading}
                disabled={mode === 'edit'}
                sx={{ minWidth: 0, gridColumn: { xs: '1', sm: '1 / -1' }, ...compactInputSx, ...(mode === 'edit' ? readonlyFieldSx : {}) }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Crop Year"
                    required
                    error={cropYearError}
                  />
                )}
              />
              <TextField
                label="BOM Code"
                value={draft.bom_code}
                onChange={(e) => setDraft({ ...draft, bom_code: e.target.value })}
                size="small"
                required
                disabled={mode === 'edit'}
                error={bomCodeError}
                sx={{ minWidth: 0, ...compactInputSx, ...(mode === 'edit' ? readonlyFieldSx : {}) }}
              />
              <Autocomplete
                freeSolo
                size="small"
                options={bomTypeOptions}
                value={draft.bom_type}
                onChange={(_, v) => setDraft({ ...draft, bom_type: v ?? '' })}
                onInputChange={(_, v) => setDraft({ ...draft, bom_type: v })}
                loading={optionsLoading}
                sx={{ minWidth: 0, ...compactInputSx }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="BOM Type"
                    required
                    error={bomTypeError}
                  />
                )}
              />
              <TextField
                label="Moisture Loss"
                value={draft.moisture_loss}
                onChange={(e) => setDraft({ ...draft, moisture_loss: e.target.value })}
                size="small"
                sx={{ minWidth: 0, ...compactInputSx }}
                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              <TextField
                label="Processing Loss"
                value={draft.processing_loss}
                onChange={(e) => setDraft({ ...draft, processing_loss: e.target.value })}
                size="small"
                sx={{ minWidth: 0, ...compactInputSx }}
                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              <TextField
                label="Note"
                value={draft.note}
                onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                size="small"
                multiline
                minRows={2}
                sx={{ minWidth: 0, gridColumn: { xs: '1', sm: '1 / -1' }, ...compactInputSx }}
              />
            </Box>

            <Divider />

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems="stretch"
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <QualityPicker
                  title="Quality - Input"
                  rows={draft.inputs}
                  pickerOptions={qualityOptions}
                  error={inputsError}
                  onChange={(rows) => setDraft({ ...draft, inputs: rows })}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <QualityPicker
                  title="Quality - Output"
                  rows={draft.outputs}
                  pickerOptions={qualityOptions}
                  error={outputsError}
                  onChange={(rows) => setDraft({ ...draft, outputs: rows })}
                />
              </Box>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}
